/**
 * AssessmentRecapModal — preview + send (or resend) the assessment recap email.
 *
 *  • Pulls server-rendered groups (one per parent) from /preview.
 *  • Shows a list of parent recipients in the left rail; right pane renders
 *    the exact HTML that will land in their inbox.
 *  • Coach picks ONE recommended pack per recipient via the dropdown — the
 *    pack glows red in the email + the CTA pivots to "Lock in this pack".
 *  • Pricing is live: PACK_CATALOG + sub_12wk membership are auto-rendered
 *    in the email at send time. No more stored plain-text pricing block.
 *
 * Two modes:
 *  • "send"   — initial recap. Fires POST /send. Sends to ALL eligible groups.
 *  • "resend" — completed session. Coach picks which parents to resend to via
 *               checkbox. Fires POST /resend-recap with selected parent_keys.
 *               Each recipient shows its send history (initial + prior resends).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Send, AlertTriangle, Mail, Star, RotateCcw, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssessmentRecapModal({ sessionId, mode = "send", onClose, onSent }) {
    const isResend = mode === "resend";
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);
    const [sending, setSending] = useState(false);
    const [savingRec, setSavingRec] = useState(false);
    const [selected, setSelected] = useState({}); // parent_key -> bool (resend mode)

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/coach/assessment-sessions/${sessionId}/preview`,
                { withCredentials: true },
            );
            setData(r.data);
            setActiveIdx(0);
            // In resend mode, default-select nobody — coach explicitly opts in
            // each parent they want to re-email. Safer than auto-selecting all.
            if (mode === "resend") {
                setSelected({});
            }
        } catch (e) {
            toast.error("Couldn't load preview", { description: e?.response?.data?.detail });
            onClose?.();
        } finally {
            setLoading(false);
        }
    }, [sessionId, onClose, mode]);

    useEffect(() => { load(); }, [load]);

    const saveRecommendation = async (parentKey, packKey) => {
        setSavingRec(true);
        try {
            await axios.put(
                `${API}/coach/assessment-sessions/${sessionId}/recommendation`,
                { parent_key: parentKey, pack_key: packKey || null },
                { withCredentials: true },
            );
            await load();
            if (packKey) {
                toast.success("Recommendation saved", {
                    description: "Email preview updated.",
                });
            } else {
                toast.success("Recommendation cleared");
            }
        } catch (e) {
            toast.error("Couldn't save recommendation", { description: e?.response?.data?.detail });
        } finally {
            setSavingRec(false);
        }
    };

    const toggleSelect = (parentKey) => {
        setSelected((prev) => ({ ...prev, [parentKey]: !prev[parentKey] }));
    };

    const send = async () => {
        setSending(true);
        try {
            let r;
            if (isResend) {
                const parent_keys = Object.entries(selected)
                    .filter(([, v]) => v)
                    .map(([k]) => k);
                if (parent_keys.length === 0) {
                    toast.error("Pick at least one recipient");
                    setSending(false);
                    return;
                }
                r = await axios.post(
                    `${API}/coach/assessment-sessions/${sessionId}/resend-recap`,
                    { parent_keys },
                    { withCredentials: true },
                );
            } else {
                r = await axios.post(
                    `${API}/coach/assessment-sessions/${sessionId}/send`,
                    {},
                    { withCredentials: true },
                );
            }
            const sent = r.data?.sent?.length || 0;
            const skipped = r.data?.skipped?.length || 0;
            if (sent === 0) {
                toast.error("Nothing was sent", {
                    description: skipped > 0
                        ? "Every selected group was skipped (missing email or no results)."
                        : "No recipients to email.",
                });
            } else {
                toast.success(
                    `${isResend ? "Re-sent" : "Sent"} ${sent} email${sent === 1 ? "" : "s"}`,
                    { description: skipped ? `${skipped} skipped — see roster for follow-ups.` : undefined },
                );
            }
            onSent?.(r.data);
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    const groups = useMemo(() => data?.groups || [], [data]);
    const sendable = useMemo(
        () => groups.filter((g) => !g.missing_email && g.athletes?.some((a) => a.has_any_result)),
        [groups],
    );
    const skippable = useMemo(
        () => groups.filter((g) => g.missing_email || !g.athletes?.some((a) => a.has_any_result)),
        [groups],
    );
    const active = groups[activeIdx] || null;
    const pricingOptions = data?.pricing_options || [];
    const selectedCount = useMemo(
        () => Object.values(selected).filter(Boolean).length,
        [selected],
    );

    if (loading || !data) {
        return (
            <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="recap-modal-loading">
                <Loader2 className="w-7 h-7 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div
            data-testid="recap-modal"
            data-mode={mode}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
            >
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">
                            {isResend ? "Resend recap" : "Send recap"}
                        </div>
                        <h3 className="font-display text-2xl tracking-wider uppercase mt-0.5">
                            {isResend ? "Pick recipients & resend" : "Preview & confirm"}
                        </h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="recap-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {groups.length === 0 ? (
                    <div className="p-10 text-center text-zinc-300 flex-1 min-h-0">
                        No participants on this assessment.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] flex-1 min-h-0 overflow-hidden">
                        {/* Recipients rail */}
                        <div className="border-r border-zinc-800 p-3 space-y-2 overflow-y-auto min-h-0">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 px-2 pt-1">
                                {isResend
                                    ? `Recipients · ${selectedCount} selected`
                                    : `Recipients (${sendable.length})`}
                            </div>
                            {groups.map((g, i) => {
                                const hasResults = g.athletes?.some((a) => a.has_any_result);
                                const skipped = g.missing_email || !hasResults;
                                const hasRec = !!g.recommended_pack_key;
                                const history = g.send_history || [];
                                const lastSent = history.length > 0 ? history[history.length - 1] : null;
                                const isSelected = !!selected[g.parent_key];
                                return (
                                    <button
                                        type="button"
                                        key={g.parent_key || i}
                                        data-testid={`recap-recipient-${i}`}
                                        onClick={() => setActiveIdx(i)}
                                        className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                                            activeIdx === i
                                                ? "border-brand-red bg-brand-red/10"
                                                : "border-zinc-800 hover:border-zinc-600 bg-zinc-900/30"
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            {isResend && (
                                                <span
                                                    role="checkbox"
                                                    aria-checked={isSelected}
                                                    data-testid={`recap-resend-toggle-${i}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (skipped) return;
                                                        toggleSelect(g.parent_key);
                                                    }}
                                                    className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer ${
                                                        skipped
                                                            ? "border-zinc-700 bg-zinc-900 cursor-not-allowed opacity-40"
                                                            : isSelected
                                                                ? "border-brand-red bg-brand-red"
                                                                : "border-zinc-600 bg-zinc-900 hover:border-brand-red"
                                                    }`}
                                                >
                                                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                                </span>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-white truncate flex items-center gap-1.5">
                                                    {hasRec && <Star className="w-3 h-3 text-brand-red shrink-0" fill="currentColor" />}
                                                    <span className="truncate">{g.parent_name}</span>
                                                </div>
                                                <div className="text-[11px] text-zinc-400 truncate">
                                                    {g.parent_email || "— no email on file —"}
                                                </div>
                                                <div className="text-[10px] mt-1 tracking-[0.12em] uppercase">
                                                    {skipped ? (
                                                        <span className="text-amber-400">
                                                            {g.missing_email ? "Skip · no email" : "Skip · no results"}
                                                        </span>
                                                    ) : history.length > 0 ? (
                                                        <span className="text-brand-navy-200">
                                                            Sent {history.length}×{lastSent?.sent_at
                                                                ? ` · ${new Date(lastSent.sent_at).toLocaleDateString()}`
                                                                : ""}
                                                        </span>
                                                    ) : (
                                                        <span className="text-emerald-400">
                                                            Ready · {g.athletes?.length || 0} athlete{g.athletes?.length === 1 ? "" : "s"}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Preview pane */}
                        <div className="p-4 overflow-y-auto min-h-0">
                            {active && (
                                <>
                                    <div className="border border-zinc-800 rounded-lg bg-zinc-900/40 px-4 py-3 mb-3">
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Subject</div>
                                        <div className="text-sm text-white mt-1 break-words">{active.subject}</div>
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-3">To</div>
                                        <div className="text-sm text-white mt-1 inline-flex items-center gap-2">
                                            <Mail className="w-3.5 h-3.5 text-brand-red" />
                                            {active.parent_email || <span className="text-amber-400">— no email on file —</span>}
                                        </div>
                                        {(active.send_history || []).length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-zinc-800">
                                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5">
                                                    Send history
                                                </div>
                                                <ul className="space-y-1 text-[11px] text-zinc-400">
                                                    {(active.send_history || []).map((h, hi) => (
                                                        <li key={hi} className="flex items-center gap-2">
                                                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${h.kind === "initial" ? "bg-emerald-400" : "bg-brand-navy-300"}`} />
                                                            <span className="text-white">
                                                                {h.kind === "initial" ? "Initial send" : "Resend"}
                                                            </span>
                                                            <span>·</span>
                                                            <span>{h.sent_at ? new Date(h.sent_at).toLocaleString() : "—"}</span>
                                                            <span>·</span>
                                                            <span>by {h.sent_by_name || "Coach"}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {/* Recommendation picker — the entire "lead them to what's best" UX */}
                                    <div className="border border-brand-red/40 bg-brand-red/5 rounded-lg px-4 py-3 mb-3">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <Star className="w-3.5 h-3.5 text-brand-red" fill="currentColor" />
                                            <span className="text-[10px] tracking-[0.25em] uppercase text-brand-red font-semibold">
                                                Recommend a pack for {active.parent_name?.split(" ")[0] || "this parent"}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-zinc-400 mb-2 leading-relaxed">
                                            Pick the pack you'd suggest based on today's assessment. It'll glow red at the top of the email with a one-click CTA. Coach picks make 3-5× the conversion of "here's our pricing."
                                        </p>
                                        <select
                                            data-testid="recap-recommend-pack"
                                            value={active.recommended_pack_key || ""}
                                            disabled={savingRec || !active.parent_key}
                                            onChange={(e) => saveRecommendation(active.parent_key, e.target.value)}
                                            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red disabled:opacity-50"
                                        >
                                            <option value="">— No specific recommendation (show full pricing only) —</option>
                                            {pricingOptions.map((opt) => (
                                                <option key={opt.key} value={opt.key}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div
                                        data-testid="recap-preview-html"
                                        className="rounded-lg border border-zinc-800 overflow-hidden bg-black"
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{ __html: active.html }}
                                    />
                                </>
                            )}

                            {/* Live pricing note — replaces the old editable pricing block */}
                            <div className="mt-5 border-t border-zinc-800 pt-4">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-2">
                                    Pricing source
                                </div>
                                <p className="text-[12px] text-zinc-400 leading-relaxed">
                                    Pricing rendered in the email is <span className="text-white font-semibold">live</span> —
                                    pulled from PACK_CATALOG + the $12/wk membership at send time.
                                    Update prices in the catalog and the next recap reflects them automatically.
                                </p>
                                {data.pricing?.packs?.length > 0 && (
                                    <div className="mt-2 text-[11px] text-zinc-500">
                                        {data.pricing.packs.length} packs · {data.pricing.membership ? "1 membership" : "no membership"} · tax {(data.pricing.tax_rate * 100).toFixed(2)}%
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between gap-2 flex-wrap shrink-0 bg-zinc-950 rounded-b-2xl">
                    <div className="text-[11px] text-zinc-400">
                        {skippable.length > 0 && !isResend && (
                            <span className="inline-flex items-center gap-1 text-amber-400">
                                <AlertTriangle className="w-3 h-3" />
                                {skippable.length} group{skippable.length === 1 ? "" : "s"} will be skipped.
                            </span>
                        )}
                        {isResend && (
                            <span className="inline-flex items-center gap-1 text-zinc-400">
                                <RotateCcw className="w-3 h-3" />
                                Check the box next to each parent to include them.
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={onClose} disabled={sending} className="btn-ghost-tactical text-xs">
                            Cancel
                        </button>
                        <button
                            type="button"
                            data-testid={isResend ? "recap-resend" : "recap-send"}
                            onClick={send}
                            disabled={
                                sending ||
                                (isResend ? selectedCount === 0 : sendable.length === 0)
                            }
                            className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {sending ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : isResend ? (
                                <RotateCcw className="w-3.5 h-3.5" />
                            ) : (
                                <Send className="w-3.5 h-3.5" />
                            )}
                            {isResend
                                ? `Resend ${selectedCount} recap${selectedCount === 1 ? "" : "s"}`
                                : `Send ${sendable.length} recap${sendable.length === 1 ? "" : "s"}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
